import { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

export function VerifyEmail() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const email = (location.state as any)?.email || "";
  const token = searchParams.get("token");

  const [loading, setLoading] = useState(!!token);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Auto-verify if token is present in URL
  useEffect(() => {
    if (token) {
      verifyEmail(token);
    }
  }, [token]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const verifyEmail = async (verificationToken: string) => {
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`/api/auth/verify-email?token=${verificationToken}`, {
        method: "GET",
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate("/login", {
            state: { message: "Email verified! You can now log in." },
          });
        }, 3000);
      } else {
        setError(data.detail || "Verification failed. The link may be expired or invalid.");
      }
    } catch (err) {
      setError("Verification failed. Please try again.");
      console.error("Email verification error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      setError("Email address not found. Please try registering again.");
      return;
    }

    setResending(true);
    setError("");

    try {
      const response = await fetch("/api/auth/resend-verification", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setResendCooldown(60); // 60 second cooldown
        alert("Verification email sent! Please check your inbox.");
      } else {
        setError(data.detail || "Failed to resend verification email.");
      }
    } catch (err) {
      setError("Failed to resend verification email. Please try again.");
      console.error("Resend verification error:", err);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#282828] via-[#4c1d95] to-[#282828] py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-6 p-10 bg-[#282828]/80 backdrop-blur-sm rounded-2xl border border-[#1283EB]/30 shadow-2xl">
        {/* Logo/Header */}
        <div className="text-center">
          <img
            src="/aiia-logo-white.svg"
            alt="AIIA Logo"
            className="h-16 mx-auto mb-4"
          />
          <h2 className="text-xl text-[#F8F3EC]">
            {loading
              ? "Verifying Your Email..."
              : success
              ? "Email Verified!"
              : "Check Your Email"}
          </h2>
        </div>

        {/* Loading State */}
        {loading && !error && (
          <div className="space-y-4">
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1283EB]"></div>
            </div>
            <p className="text-center text-white/60">
              Please wait while we verify your email address...
            </p>
          </div>
        )}

        {/* Success State */}
        {success && (
          <div className="space-y-4">
            <div className="flex justify-center">
              <div className="rounded-full bg-green-500/20 p-3">
                <svg
                  className="w-12 h-12 text-green-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            </div>
            <div className="text-center space-y-2">
              <p className="text-white/80">
                Your email has been successfully verified!
              </p>
              <p className="text-sm text-white/60">
                Redirecting you to the login page...
              </p>
            </div>
          </div>
        )}

        {/* Waiting for Verification State */}
        {!loading && !success && !error && email && (
          <div className="space-y-4">
            <div className="flex justify-center">
              <div className="rounded-full bg-[#1283EB]/20 p-3">
                <svg
                  className="w-12 h-12 text-[#1283EB]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
            </div>

            <div className="text-center space-y-3">
              <p className="text-white/80">
                We've sent a verification email to:
              </p>
              <p className="text-[#1283EB] font-medium break-all">{email}</p>
              <p className="text-sm text-white/60">
                Click the link in the email to verify your account and complete registration.
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-lg p-4 space-y-2">
              <p className="text-xs text-white/70 font-medium">
                What to do next:
              </p>
              <ul className="text-xs text-white/60 space-y-1">
                <li>1. Check your inbox (and spam folder)</li>
                <li>2. Look for an email from aiia</li>
                <li>3. Click the "Verify Email Address" button</li>
                <li>4. You'll be redirected to log in</li>
              </ul>
            </div>

            {/* Resend Button */}
            <div className="pt-4 border-t border-white/10">
              <p className="text-sm text-white/60 text-center mb-3">
                Didn't receive the email?
              </p>
              <button
                onClick={handleResendVerification}
                disabled={resending || resendCooldown > 0}
                className="w-full py-2 px-4 text-sm font-medium rounded-lg text-white/90 bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-[#1283EB] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resending
                  ? "Sending..."
                  : resendCooldown > 0
                  ? `Resend in ${resendCooldown}s`
                  : "Resend Verification Email"}
              </button>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="space-y-4">
            <Alert variant="destructive" className="bg-red-900/20 border-red-800 text-red-400" role="alert">
              <AlertCircle className="h-4 w-4" aria-hidden="true" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>

            {email && (
              <button
                onClick={handleResendVerification}
                disabled={resending || resendCooldown > 0}
                className="w-full py-2 px-4 text-sm font-medium rounded-lg text-white bg-[#1283EB] hover:bg-[#1283EB]/90 focus:outline-none focus:ring-2 focus:ring-[#1283EB] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resending
                  ? "Sending..."
                  : resendCooldown > 0
                  ? `Resend in ${resendCooldown}s`
                  : "Try Resending Email"}
              </button>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="text-center pt-4 border-t border-white/10">
          <p className="text-sm text-white/60">
            Need help?{" "}
            <a
              href="mailto:support@aiia.io"
              className="text-[#1283EB] hover:text-[#1283EB]/80 font-medium transition-colors"
            >
              Contact support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default VerifyEmail;
